import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {BaseAreaView, IView, IViewsContainer, OBaseAreaViewInit, OViewVisitOptions} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspUiEnv, WSP, Wsp} from "/@lib@/wsp/wsp.js";
import {OTabsInit, Tab, Tabs} from "/@back@/commons/widgets/tabs.js";
import {SimpleTagArea} from "/@lib@/commons/areas.js";
import {ActionBtn, Button, OActionBtnInit} from "/@back@/commons/widgets/buttons.js";
import {FocusItem, OpenSearchItems} from "/@back@/wsp/actions/srcActions.js";
import {ISearchFunc, ISearchPersist, ISearchStore, ISearchViewResult, SearchRequest} from "/@lib@/wsp/search.js";
import {IUiCritArea, OUiCritInit, UiCritBool} from "/@back@/wsp/widgets/search/uiCrit.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {OSrcGridInit, SrcGrid} from "/@back@/wsp/widgets/srcGrid.js";
import {BASIS, PickInit} from "/@back@/commons/basis.js";
import {OSearchItemsInit, SearchItems} from "/@back@/wsp/dialogs/searchItems.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {AccelKeyMgr, Action, ActionMenuDep, IAction} from "/@lib@/commons/actions.js";
//déclaration des Area pour l'abstract.
import "/@back@/wsp/widgets/search/itemCrit.js";
import "/@back@/wsp/widgets/search/taskCrit.js";
import {IO} from "/@lib@/commons/io/io.js";
import {JSrcFields, srcUri} from "/@lib@/wsp/src.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {IWedSearchCoordinatorPointer} from "/@back@/edit/wed/features/searchBar.js";
import {ExportResultCsv_item, ExportResultCsv_task, ExportResultItems} from "/@back@/wsp/actions/searchesActions.js";
import "/@back@/wsp/views/views_Perms.js";
import {OAppHeaderInit} from "/@back@/core/widgets/appHeader.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";

/**
 */
export interface Searches extends BaseAreaView<IRegPointer<IWspUiEnv>> {
	initialize(init: OSearchesInit): this
}

export interface OSearchesInit extends OBaseAreaViewInit<IRegPointer<IWspUiEnv>>,
	PickInit<Searches, 'criterions' | 'privateUri' | 'kind'> {

	/** Init personnalisé de la barre d'actions principale de cette vue de recherche  */
	barActionsInit?: OBarActionsInit<Searches>

	results?: OSearchResultOptions
}

/**
 * View d'une recherche
 *
 * <br> Déclaration de 'recherches' dans la biibliothèque via des objets
 *  de type {@link ISearchPersist} sur la liste associée à `actionsSearchPersistLists`
 * Exemple :
 * <pre>
 * REG.reg.addToList("requests:item", "test1", 1, {
 * 		title : "Requete prédéfinie 1",
 * 		whereCrit: <where><exp type="Items" exclude="/~annot/;/~air/;/~ext/" uiCrit="wsp-itemcrit-fullwsp"/><and><exp type="FullText" textSearch="11" options=";c;" uiCrit="wsp-itemcrit-fulltext"/></and></where>
 * 	} as ISearchPersist);
 * </pre>
 *
 */
export class Searches extends BaseAreaView<IRegPointer<IWspUiEnv>> implements ISearchStore, IViewsContainer, ISearchViewResult {
	reg: IReg<IWspUiEnv>;
	wsp: Wsp;
	criterions: Dict<IUiCritArea>;
	privateUri: srcUri;
	resultsOptions: OSearchResultOptions;

	kind: 'item' | 'task'

	/** Date de dernière modif des requetes enregistrées. */
	srcDt = -1;

	tabs: Tabs;

	/**
	 * Promesse résolue lorsque les requêtes persistante ont été chargées.
	 * note : on ne peut pas utiliser un BaseAreaViewAsync car le chargement est lazy sur le 1er onShown.
	 */
	available: Promise<void>;

	/** Appelé par le dialogue d'édition des requêtes. */
	async persistSearch(search: ISearchPersist): Promise<void> {
		if (!this.available) this.available = this._fetchSearches();
		await this.available;
		const promise = this._addOrUpdateTab(search.whereCrit instanceof Element ? search.whereCrit : search.whereCrit(this), search.id, search.title, search.description, true, true);
		this._saveSearches();
		return promise;
	}

	/** Visualisation d'une recherche dans un onglet */
	protected async _addOrUpdateTab(whereCrit: Element, searchId?: string, searchLabel?: string, searchDescription?: string, persist = false, select = false): Promise<void> {
		const tab = searchId ? this.tabs.getTabByCode(searchId) : null;
		if (tab) {
			(tab.area as SearchResultArea).setWhereCrit(whereCrit);
			(tab.area as SearchResultArea).setLabel(searchLabel);
			tab.refresh();
			if (tab.view) (tab.view as SearchResult).refresh();
			if (select) await this.tabs.selectTab(tab);
		} else {
			if (!searchId) searchId = this.newId();
			const currentTabId = this.tabs.selectedTab?.id;
			// Ordre des tabs : persistants, puis non persistants
			if (persist) {
				let persistTabs = this.tabs.findTabs((tab) => tab.area instanceof SearchResultPersistArea) || [];
				await this.tabs.addTab(new SearchResultPersistArea({title: searchLabel, whereCrit, id: searchId}, this.resultsOptions).setDescription(searchDescription), {select, position: persistTabs.length});
				const newTab = this.tabs.getTabByCode(searchId);
				DOM.addClass(newTab, 'reqPersistant');
			} else
				await this.tabs.addTab(new SearchResultArea(searchId, this.resultsOptions).setLabel(searchLabel).setDescription(searchDescription).setWhereCrit(whereCrit), {select, position: this.tabs._tabs.childElementCount});
		}
	}

	async viewSearch(whereCrit: Element, searchId?: string, searchLabel?: string, searchDescription?: string): Promise<string> {
		if (!searchId) searchId = this.newId();
		await this._addOrUpdateTab(whereCrit, searchId, searchLabel, searchDescription, false, true);
		return searchId;
	}

	async deleteSearch(id: string): Promise<boolean> {
		const tab = this.tabs.getTabByCode(id);
		if (!tab) return false;
		if (!(tab.area instanceof SearchResultPersistArea) || await POPUP.confirm("Confirmez la suppression de cette recherche", this, {okLbl: "Supprimer"})) {
			if (await this.tabs.closeTab(tab, true, true)) await this._saveSearches();
		}
		return true;
	}

	protected newId() {
		let id;
		do {
			id = Math.abs(Math.random() * 99999).toString(36);
		} while (this.tabs.getTabByCode(id) != null);
		return id;
	}

	protected _initialize(init: OSearchesInit) {
		super._initialize(init); //init this.reg, this.area, this.areaContext
		this.wsp = this.reg.env.wsp;
		this.criterions = init.criterions;
		this.resultsOptions = init.results;
		this.privateUri = init.privateUri;
		this.kind = init.kind || 'item';

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.tabs = sr.appendChild(<Tabs id="tabs" î={{
			reg: this.reg,
			areasContext: this,
			backgroundPanels: async (tabs) => <div style="flex:1; display: flex; justify-items: center; align-items: center; justify-content: center;">
				<ActionBtn î={{
					label: this.kind === "task" ? "Nouvelle recherche de tâches" : "Nouvelle recherche d'items",
					uiContext: "dialog",
					action: new OpenSearchItems(this.kind),
					actionContext: this
				} satisfies OActionBtnInit<IRegPointer<IWspUiEnv>>}/>
			</div>,
			skinOver: "wsp-searches/tabs"
		} as OTabsInit<Searches>}/>) as Tabs;

		this.tabs._initTab = function (tab: Tab<Searches>) {
			tab.movable = this.movable;
			tab.onclick = this._onClickTab;
			tab._createLabel();
			tab._labelElt.ondblclick = tab.areaContext.onDblclickTab;
			tab.shadowRoot.appendChild(tab.areaContext._newDelButton());
		};

		this.tabs.appendChild(<BarActions slot="tools-end" î={Object.assign({
			reg: this.reg,
			actionContext: this,
			uiContext: "bar"
		} as OBarActionsInit<Searches>, init.barActionsInit)}/> as BarActions<Searches>);

	}

	_newDelButton(): Button {
		return <Button onclick={this.onRemoveTab} tabindex-custom="-1" icon="/@skin@/commons/icons/close.svg" ui-context="bar" title="Supprimer cette recherche"/> as Button;
	}

	protected onDblclickTab(this: HTMLElement, ev: MouseEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const tab = DOMSH.findHost<Tab<Searches>>(this);
		editSearch(tab.area as SearchResultArea, tab.areaContext, tab, tab.areaContext.kind);
	}

	protected onRemoveTab(this: Button, ev: MouseEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		const tab = DOMSH.findHost<Tab<Searches>>(this);
		tab.areaContext.deleteSearch(tab.code);
	}

	buildWhereXml(): Element {
		return (this.tabs.selectedTab?.area as SearchResultArea).getWhereCrit(this);
	}

	async _fetchSearches() {
		try {
			if (!this.wsp.isAvailable) await this.wsp.waitForAvailable(this);
			//console.log("_fetchSearches:::" + this.privateUri);
			const orderedTabs = this.tabs.areas && this.tabs.areas.length > 0 ? [] as string[] : null;
			const resp = await this.wsp.wspServer.config.privateFolderUrl.fetch(IO.qs("cdaction", "GetSrc", "format", "stream", "fields", "srcDt", "param", this.wsp.code, "refUri", "", "privateUri", this.privateUri), 'json');
			if (resp.status === 200) {
				this.srcDt = (JSON.parse(resp.headers.get("X-SCFIELDS")) as JSrcFields).srcDt;
				const savedSearch = resp.asJson as JSearchesDatas;
				//console.log("savedSearch::::::::", this.srcDt, savedSearch);
				if (savedSearch && savedSearch.searches) for (const search of savedSearch.searches) {
					if (!search) continue;
					const doc = DOM.parseDomValid(search.whereXml);
					if (!doc) continue;
					if (orderedTabs) orderedTabs.push(search.id);
					this._addOrUpdateTab(doc.documentElement, search.id, search.title, null, true, false);
				}
			} else {
				this.srcDt = -1;
			}
			//Gestion de l'ordonnancement des tabs et du cleanup des recherches supprimées.
			if (orderedTabs) {
				// On récupère les tabs 'custom' (non mémorisées) ouvertes
				this.tabs.areas.forEach((entry) => {
					if (!(entry instanceof SearchResultPersistArea)) orderedTabs.push(entry.getId());
				});
				this.tabs.reorderTabs(true, ...orderedTabs);
			}
		} catch (e) {
			this.srcDt = -1;
			ERROR.log("fetchSearches failed", e);
		}
	}

	async _saveSearches() {
		try {
			const searches: JSearchDatas[] = [];
			this.tabs.areas.forEach((area: SearchResultPersistArea | SearchResultArea) => {
				if (area instanceof SearchResultPersistArea)
					searches.push({id: area.getId(), title: area.getLabel(this), whereXml: DOM.ser(area.getWhereCrit(this))});
			});
			const fields = await this.wsp.wspServer.config.privateFolderUrl.fetchJson<JSrcFields>(IO.qs("cdaction", "PutSrc", "format", "JSON", "fields", "srcDt", "param", this.wsp.code, "refUri", "", "privateUri", this.privateUri), {
				method: "POST",
				body: JSON.stringify({searches}),
				headers: {"Content-Type": "application/json"}
			});
			this.srcDt = fields.srcDt;
			this.reg.env.universe.wsFrames.ws.postMsg({svc: 'liaise', type: "searches.saved", uri: this.privateUri})
		} catch (e) {
			ERROR.log(`Save searches to '${this.privateUri}' failed`, e);
			POPUP.showNotifError("Les modifications de votre liste de recherches n'ont pas pu être enregistrées.", this);
		}
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		this.tabs.visitViews(visitor, options);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.tabs.visitViewsAsync(visitor, options);
	}

	_shown: boolean;

	onViewHidden(closed?: boolean): void {
		this._shown = false;
		if (closed) {
			this.reg.env.universe.wsFrames.ws.msgListeners.removeListener('liaise', this._onLiaiseMsg);
			this.reg.env.place.eventsMgr.removeListener("onConnectionRenewed", this._onConnRenewed);
		}
	}

	onViewShown() {
		this._shown = true;
		if (!this._onLiaiseMsg) {
			this._onLiaiseMsg = this.onLiaiseMsg.bind(this);
			this.reg.env.universe.wsFrames.ws.msgListeners.on('liaise', this._onLiaiseMsg);
		}
		if (!this._onConnRenewed) {
			this._onConnRenewed = this.onConnRenewed.bind(this);
			this.reg.env.place.eventsMgr.on("onConnectionRenewed", this._onConnRenewed);
		}
		if (!this.available) this.available = this._fetchSearches();
	}

	protected onSyncLost() {
		if (this._shown) {
			//On est visible, on refresh
			this.available = this._fetchSearches();
		} else {
			//On est masqué, on marque l'état invalide.
			this.available = null;
		}
	}

	/** Détection d'une écriture concurente via le service de liaise. */
	protected _onLiaiseMsg: (m: Jsonisable) => void;

	protected onLiaiseMsg(m: Jsonisable) {
		if (m.type === 'searches.saved' && m.uri === this.privateUri) this.onSyncLost();
	}

	protected _onConnRenewed: () => void;

	protected async onConnRenewed() {
		const resp = await this.wsp.wspServer.config.privateFolderUrl.fetch(IO.qs("cdaction", "GetSrc", "format", "JSON", "fields", "srcDt", "param", this.wsp.code, "refUri", "", "privateUri", this.privateUri), 'json');
		if (resp.status === 200 ? this.srcDt !== resp.asJson.srcDt : resp.status === 404 && this.srcDt !== -1) this.onSyncLost();
	}

}

REG.reg.registerSkin("wsp-searches", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#tabs {
		flex: 1;
	}
`);

REG.reg.registerSkin("wsp-searches/tabs", 1, /* language=CSS */ `
	#panels {
		border-inline-start: none;
		border-inline-end: none;
		border-bottom: none;
	}

	c-tab {
		font-size: var(--label-size);
	}

	#head {
		min-height: 2em;
	}

	c-tab:not(.reqPersistant) {
		font-style: italic;
	}
`);

customElements.define("wsp-searches", Searches);

/** Schéma json d'enregistrement des requetes. */
interface JSearchesDatas {
	searches: JSearchDatas[]
}

interface JSearchDatas {
	id: string
	title: string
	whereXml: string
}


/**
 * Config de l'affichage des résultats d'une requete.
 */
interface OSearchResultOptions {
	/** Nb max d'items retournés. */
	max?: number
	/** Init de la liste des résultats. */
	srcGrid?: OSrcGridInit
	/** Actions de la toolbar. */
	barActions?: IAction<SearchResult>[]
	msgs?: JSearchResultL10nMsgs
}

/**
 * Area de résultat de recherche SANS persistance
 */
export class SearchResultArea extends SimpleTagArea<ISearchResultCtx, SearchResult> {
	options: OSearchResultOptions;
	conf: OSearchResultInit;
	whereCrit?: Element | ((ctx: IRegPointer<IWspUiEnv>) => Element);

	constructor(id?: string, options?: OSearchResultOptions, init?: OSearchResultInit) {
		super('wsp-search-result', null, id);
		this.conf = init || {};
		this.options = options || {};
		if (!this.options.max) this.options.max = 2000;
	}

	setWhereCrit(whereCrit?: Element | ((ctx: IRegPointer<IWspUiEnv>) => Element)): this {
		this.whereCrit = whereCrit;
		return this;
	}

	getWhereCrit(ctx: IRegPointer<IWspUiEnv>): Element {
		return this.whereCrit instanceof Element ? this.whereCrit : this.whereCrit(ctx);
	}

	protected newElt(ctx: ISearchResultCtx, lastDatas?: JLastDatas): SearchResult {
		const elt = super.newElt(ctx, lastDatas);
		elt.initialize(Object.assign({
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
		}, this.conf));
		return elt;
	}

}

/**
 * Area de résultat de recherche AVEC persistance
 */
export class SearchResultPersistArea extends SearchResultArea {
	constructor(protected persist: ISearchPersist, options: OSearchResultOptions) {
		super(persist.id);
		this.options = options;
		if (!this.options.max) this.options.max = 2000;
		this.whereCrit = persist.whereCrit;
	}

	getLabel(ctx: ISearchResultCtx): string {
		const ovrLabel = (typeof this._label === 'function') ? this._label(ctx, this) : this._label;
		return ovrLabel || this.persist.title || "...";
	}

	getPersist(ctx: ISearchResultCtx): ISearchPersist {
		this.persist.whereCrit = this.whereCrit;
		this.persist.title = this.getLabel(ctx);
		return this.persist;
	}
}


export interface ISearchResultCtx extends OUiCritInit, ISearchStore {
}


/**
 * View d'affichage d'une requête.
 * Prévue pour être aussi utilisable en dehors du widget container Searches.
 */
export interface SearchResult extends BaseAreaView<ISearchResultCtx>, ISearchViewResult {
	initialize(init: OSearchResultInit): this
}

export interface OSearchResultInit extends OBaseAreaViewInit<ISearchResultCtx> {
	/** true par défaut */
	editable?: boolean
}

export interface JSearchResultL10nMsgs {
	/** vars : 'max' */
	resultsMore?: (max: number) => string
	resultsEmpty?: string
	resultsOne?: string
	resultsCount?: (count: number) => string
}

export class SearchResult extends BaseAreaView<ISearchResultCtx> implements ISearchViewResult {
	reg: IReg<IWspUiEnv>;
	area: SearchResultPersistArea | SearchResultArea;
	wsp: Wsp;

	protected _grid: SrcGrid;
	protected _abstract: Button | HTMLDivElement;
	protected _found: HTMLElement;
	protected _bar: BarActions<SearchResult>;

	protected _searchColumns: Map<string, string | ISearchFunc>;

	protected _currentWhere: Element;

	protected _initialize(init: OSearchResultInit) {
		const conf = Object.assign({
			editable: true,
		}, init)
		super._initialize(conf); //init this.reg, this.area, this.areaContext
		this.wsp = this.reg.env.wsp;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		//sr.appendChild(<div>{DOM.ser(this.area.persist.whereCrit)}</div>);
		this.reg.installSkin("scroll/small", sr);
		this._initAndInstallSkin(this.localName, init);
		const head = sr.appendChild(<div id="header"/>);
		this._abstract = head.appendChild(<Button id="abstract" onclick={this.onEdit}/> as Button);
		this._abstract.disabled = !conf.editable;
		this._found = head.appendChild(<span id="found"/>);
		this._bar = head.appendChild(<BarActions id="bar" î={{
			reg: this.reg,
			actionContext: this,
			actions: this.area.options.barActions,
		} as OBarActionsInit<SearchResult>}/> as BarActions<SearchResult>);
		const initGrid = BASIS.newInit(this.area.options.srcGrid, this.reg);
		if (initGrid.skinScroll === undefined) initGrid.skinScroll = 'scroll/small';
		this._grid = sr.appendChild(new SrcGrid().initialize(initGrid));
	}

	protected _refresh() {
		super._refresh();
		if (!DOM.nodeEquals(this._currentWhere, this.area.getWhereCrit(this.areaContext))) {
			this._abstract.textContent = null;
			const descTxt = this.area.getDescription(this.areaContext);
			if (descTxt)
				this._abstract.textContent = descTxt;
			else {
				const desc = UiCritBool.AREA_AND_ROOT.buildAbstract(this.area.getWhereCrit(this.areaContext), this.areaContext);
				if (desc) this._abstract.appendChild(desc);
			}
			this._grid.refresh();// indispensble pour terminer d'initialiser le tree (def des colonnes, ...)
			this._bar.refresh();
			this.doSearch();
		}
	}

	async doSearch() {
		//console.log("doSearch:::", this.area.persist.whereCrit);
		this._grid.srcGridDatas.setDatas([]);
		this._found.textContent = "En cours...";
		const currentWhere = this.buildWhereXml();
		try {
			if (!this._searchColumns) {
				this._searchColumns = new Map();
				this._grid.fillSearchColumns(this._searchColumns);
			}
			const req = new SearchRequest(this._searchColumns).setMax(this.area.options.max + 1).setWhereXml(currentWhere);
			const results = await WSP.fetchSearchJson(this.wsp, this, req.toXml());
			const count = results.length;
			const max = this.area.options.max;
			//console.log("results::::", results);
			this._grid.srcGridDatas.setDatas(results);
			if (results.length === 0) this._found.textContent = this.area.options.msgs?.resultsEmpty ? this.area.options.msgs.resultsEmpty : "Aucun item trouvé";
			else if (results.length === 1) this._found.textContent = this.area.options.msgs?.resultsOne ? this.area.options.msgs.resultsOne : "1 item trouvé";
			else if (results.length > this.area.options.max) this._found.textContent = this.area.options.msgs?.resultsMore ? this.area.options.msgs.resultsMore(max) : `Plus de ${max} items trouvés`;
			else this._found.textContent = this.area.options.msgs?.resultsCount ? this.area.options.msgs.resultsCount(count) : `${count} items trouvés`;
			this._currentWhere = currentWhere;
		} catch (e) {
			this._found.textContent = "Échec";
			ERROR.log(`Do search failed: ${DOM.ser(currentWhere)}`, e);
		}
	}

	buildWhereXml(): Element {
		return this.area.getWhereCrit(this.areaContext);
	}

	async viewSearch(whereCrit: Element, searchId: string | null, searchLabel?: string, searchDescription?: string): Promise<string> {
		console.warn('not implemented');
		return undefined;
	}

	setWedSearch() {
		const coord = (this.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord;
		if (coord && this._currentWhere) {
			coord.setParamsOnce(UiCritBool.AREA_AND_ROOT.buildWedSearch(this._currentWhere, this.areaContext.criterions) || null);
		}
	}

	onEdit(this: Button) {
		if (this.disabled) return;
		const me = DOMSH.findHost<SearchResult>(this);
		editSearch(me.area, me.areaContext, me, (me.areaContext as Searches).kind);
	}
}

/** */
export class FocusItemSetWedSearch extends FocusItem {

	execute(ctx: SrcGrid, ev?: Event) {
		const searchRes = DOMSH.findHost<SearchResult>(ctx);
		searchRes.setWedSearch();
		super.execute(ctx, ev);
	}
}


REG.reg.registerSkin("wsp-search-result", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#header {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	#abstract {
		flex: 1;
		overflow: auto;
		max-height: 2.6em;
		align-items: start;
		font-size: var(--label-size);
	}

	#found {
		font-weight: bold;
		border-inline-start: 1px solid var(--border-color);
		border-inline-end: 1px solid var(--border-color);
		padding: 0 .5em;
	}

	wsp-src-grid {
		flex: 1;
		border: none;
		border-top: 1px solid var(--border-color);
	}

	.boolOp, .boolBracket {
		color: var(--alt1-color);
	}

	.critVal {
		font-weight: bold;
	}

`);

customElements.define("wsp-search-result", SearchResult);

async function editSearch(search: SearchResultPersistArea | SearchResultArea, ctx: ISearchResultCtx, from: HTMLElement, kind: 'item' | 'task' = 'item') {
	const {SearchItems} = await import("/@back@/wsp/dialogs/searchItems.js");
	const {TplRequestsMenu} = await import("/@back@/wsp/actions/searchesActions.js");

	const forcedSearchRequest = search instanceof SearchResultPersistArea ? search.getPersist(ctx) : search.getWhereCrit(ctx);
	if (!forcedSearchRequest)
		if (kind === 'item')
			ctx.reg.addToList("actions:searchItems:bar:item", "tplRequestsMenu", 1, TplRequestsMenu.makeFromSearchPersistLists(ctx, "requests:item", "requests:item:searchItems:item"));
		else
			ctx.reg.addToList("actions:searchItems:bar:task", "tplRequestsMenu", 1, TplRequestsMenu.makeFromSearchPersistLists(ctx, "requests:task", "requests:item:searchItems:task"));

	const ct = new SearchItems().initialize({
		reg: ctx.reg,
		showMainView: true,
		srcGrid: {
			actions: kind === 'item' ?
				ctx.reg.mergeLists("actions:wsp:shortDesc", "actions:searchItem:shortDesc:item")
				: ctx.reg.mergeLists("actions:wsp:shortDesc:task", "actions:searchItem:shortDesc:task"),
			accelKeyMgr: new AccelKeyMgr().initFromMapActions(kind === 'item' ?
				ctx.reg.mergeListsAsMap("accelkeys:searchItem:shortDesc", "accelkeys:wsp:shortDesc:item")
				: ctx.reg.mergeListsAsMap("accelkeys:wsp:shortDesc:task", "accelkeys:wsp:shortDesc:task")),
		},
		criterions: ctx.criterions,
		appHeader: {
			mainActions: ctx.reg.mergeLists(SearchItems.APPBAR_MAIN_ACTIONS, kind === 'item' ? "actions:searchItems:bar:item" : "actions:searchItems:bar:task"),
		} as OAppHeaderInit<any>,
		searchStore: search instanceof SearchResultPersistArea ? ctx : null,
		msgs: kind === 'item' ? undefined : {
			resultsMore: max => `Plus de ${max} tâches trouvées`,
			resultsEmpty: 'Aucune tâche trouvée',
			resultsOne: '1 tâche trouvée',
			resultsCount: count => `${count} tâches trouvées`,
		},
		canEditTitle: search instanceof SearchResultPersistArea ? true : false,
		initSearchRequest: forcedSearchRequest,
	} as OSearchItemsInit);
	const searchTitle = search.getLabel(ctx);

	POPUP.showDialog(ct, from, {
		titleBar: {
			barLabel: {
				label: kind === 'item'
					? (searchTitle ? `Édition de la recherche d'items '${searchTitle}'` : "Édition de la recherche d'items")
					: (searchTitle ? `Édition de la recherche de tâches '${searchTitle}'` : "Édition de la recherche de tâches")
			}, closeButton: {}
		},
		resizer: {},
		viewPortX: "middle",
		viewPortY: "middle",
		initWidth: "90vw",
		initHeight: "90vh"
	});
}

REG.reg.addToList("actions:wspApp:searches", "refresh", 1, new Action<SearchResult>('refresh')
	.setLabel("Recommencer la recherche")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute((ctx: SearchResult) => {
		ctx.doSearch();
	}), 10);

REG.reg.addToList("actions:wspApp:searches:item", "mainburger", 1, new ActionMenuDep<SearchResult>('mainburger')
	.setLabel("Actions complémentaires sur la recherche")
	.setIcon("/@skin@/commons/icons/hamburger.svg")
	.setActionLists('actions:wspApp:searches:item:mainburger')
	.setGroup("burger"), 10000);
REG.reg.addToList("actions:wspApp:searches:item:mainburger", "actionExportResultCsv", 1, new ExportResultCsv_item().requireVisiblePerm("ui.searches.export.props"));
REG.reg.addToList("actions:wspApp:searches:item:mainburger", "actionExportResultItems", 1, new ExportResultItems().requireVisiblePerm("ui.searches.export.items"));

REG.reg.addToList("actions:wspApp:searches:task", "mainburger", 1, new ActionMenuDep<SearchResult>('mainburger')
	.setLabel("Actions complémentaires sur la recherche")
	.setIcon("/@skin@/commons/icons/hamburger.svg")
	.setActionLists('actions:wspApp:searches:task:mainburger'), 1);
REG.reg.addToList("actions:wspApp:searches:task:mainburger", "actionExportResultCsv", 1, new ExportResultCsv_task().requireVisiblePerm("ui.searches.export.props"));


REG.reg.addToList("actions:wspApp:searches", "del", 1, new Action<SearchResult>('del')
	.setLabel("Supprimer")
	.setIcon("/@skin@/commons/icons/delete.svg")
	.setExecute((ctx: SearchResult) => {
		const searches = DOMSH.findHost<Searches>(ctx);
		searches.deleteSearch(ctx.area.getId());
	}), 9999);
